import { Stack, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { Select } from '@/components/Form/Select';
import { Switch } from '@/components/Form/Switch';
import { TextInput } from '@/components/Form/TextInput';
import { Icon } from '@/components/Icon/Icon';

import { COUNTRIES_WITH_POPULAR } from '@/constants/countries';
import { MinorAddressFormType } from '@/constants/minor';
import { getTenantConfigs } from '@/constants/tenant-configs';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { TaxResidencyTooltipContent } from '@/pages/onboarding/address-information/components/AddressForm/TaxResidencyTooltip';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectConfigsState } from '@/redux/configs';
import { selectOperationData } from '@/redux/operation';

import { CountryISO } from '@/types/places';

import { calculateTinVisibility } from './helper';

type MinorTaxResidencySectionProps = {
  isSubmitting: boolean;
};

export function MinorTaxResidencySection({
  isSubmitting,
}: Readonly<MinorTaxResidencySectionProps>) {
  const { t, country: translatedCountry } = useTranslate();
  const { control, setValue } = useFormContext<MinorAddressFormType>();
  const adultUser = useAppSelector(selectLoggedInUser);
  const { user: minorUser } = useAppSelector(selectOperationData);
  const { requiredLocalTIN, isoCode: localISO } =
    useAppSelector(selectConfigsState);

  const tenantConfig = getTenantConfigs(adultUser!.tenant);

  // Watch form values for TIN visibility calculation
  const shareAddress = useWatch({ control, name: 'shareAddress' });
  const country = useWatch({ control, name: 'country' }) || '';
  const differentTaxResidency = useWatch({
    control,
    name: 'differentTaxResidency',
  });
  const taxResidences = useWatch({ control, name: 'taxResidences' });

  const effectiveDifferentTaxResidency = shareAddress || differentTaxResidency;

  const { shouldShowUsTin, shouldShowLocalTin, otherCountriesTin } = useMemo(
    () =>
      calculateTinVisibility({
        effectiveDifferentTaxResidency,
        taxResidences,
        country,
        requiredLocalTIN,
        localISO,
        minorUser,
      }),
    [
      effectiveDifferentTaxResidency,
      taxResidences,
      country,
      requiredLocalTIN,
      localISO,
      minorUser,
    ],
  );

  // Clear TIN fields when not needed
  useEffect(() => {
    if (!shouldShowUsTin) {
      setValue('usTin', '');
    }
    if (!shouldShowLocalTin) {
      setValue('localTin', '');
    }
    if (!otherCountriesTin?.length) {
      setValue('otherTins', {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxResidences, shouldShowUsTin, shouldShowLocalTin, otherCountriesTin]);

  const countryOptions = useMemo(
    () =>
      COUNTRIES_WITH_POPULAR.map(item => ({
        ...item,
        text: translatedCountry[item.value],
      })),
    [translatedCountry],
  );

  return (
    <>
      {!shareAddress && (
        <Stack direction="column" alignItems="stretch" gap={3}>
          <Controller
            name="differentTaxResidency"
            control={control}
            render={({ field }) => (
              <Switch
                checked={field.value}
                onChange={field.onChange}
                label={
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2">
                      {t('createNewAccount.addressPage.form.tax.label')}
                    </Typography>
                    <Tooltip
                      title={
                        <TaxResidencyTooltipContent
                          maxSelections={tenantConfig.maxTaxResidencySelection}
                        />
                      }
                      placement="top"
                      arrow
                      enterTouchDelay={0}
                    >
                      <span>
                        <Icon
                          name="info-outline"
                          color={theme => theme.palette.grey[700]}
                        />
                      </span>
                    </Tooltip>
                  </Stack>
                }
              />
            )}
          />
        </Stack>
      )}

      {effectiveDifferentTaxResidency && (
        <Stack direction="column" alignItems="stretch" gap={3}>
          <Controller
            name="taxResidences"
            control={control}
            render={({ field, fieldState }) => (
              <Select
                {...field}
                control={control}
                options={countryOptions}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                disabled={isSubmitting}
                placeholder={t(
                  'createNewAccount.addressPage.form.countryOfTax.placeHolder',
                )}
                useDrawerOnMobile
                label={t(
                  'createNewAccount.addressPage.form.countryOfTax.label',
                )}
                withSearchBar
                multiple
                maxSelections={tenantConfig.maxTaxResidencySelection}
                className="multiSelectItem"
              />
            )}
          />
        </Stack>
      )}

      {shouldShowLocalTin && (
        <Stack direction="column" alignItems="stretch" gap={3}>
          <Controller
            name="localTin"
            control={control}
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                inputMode="numeric"
                label={t('createNewAccount.addressPage.form.tin.label', {
                  value: translatedCountry[localISO],
                })}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                disabled={isSubmitting}
                fullWidth
              />
            )}
          />
        </Stack>
      )}

      {shouldShowUsTin && (
        <Stack direction="column" alignItems="stretch" gap={3}>
          <Controller
            name="usTin"
            control={control}
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                inputMode="numeric"
                label={t('createNewAccount.addressPage.form.tin.label', {
                  value: translatedCountry[CountryISO.US],
                })}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                disabled={isSubmitting}
                fullWidth
              />
            )}
          />
        </Stack>
      )}

      {otherCountriesTin?.length > 0 && (
        <Stack direction="column" alignItems="stretch" gap={3}>
          {otherCountriesTin.map(countryCode => (
            <Controller
              key={countryCode}
              name={`otherTins.${countryCode}`}
              control={control}
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  inputMode="numeric"
                  label={t('createNewAccount.addressPage.form.tin.label', {
                    value: translatedCountry[countryCode],
                  })}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  disabled={isSubmitting}
                  fullWidth
                />
              )}
            />
          ))}
        </Stack>
      )}
    </>
  );
}
